import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Building2, Home, User } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SelectRolePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(null);
  const { t } = useTranslation();

  const handleSelect = async (role) => {
    setLoading(role);
    try {
      await axios.put(`${API}/auth/google/set-role`, { user_type: role }, { withCredentials: true });
      // Update stored user
      const stored = JSON.parse(localStorage.getItem("google_user") || "{}");
      stored.user_type = role;
      localStorage.setItem("google_user", JSON.stringify(stored));

      toast.success(t("auth.accountCreatedAs", { role: role === "customer" ? t("auth.homeowner").toLowerCase() : t("auth.contractor").toLowerCase() }));
      navigate(role === "customer" ? "/customer" : "/business", { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || t("auth.failedSetRole"));
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] px-4" data-testid="select-role-page">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="w-12 h-12 rounded-xl bg-[#0B1F3B] flex items-center justify-center mx-auto mb-4">
            <Building2 className="w-7 h-7 text-[#D4AF37]" />
          </div>
          <h1 className="text-2xl font-extrabold text-[#0B1F3B]" style={{ fontFamily: 'Manrope' }}>{t("auth.welcomeToApp")}</h1>
          <p className="text-slate-500 mt-2">{t("auth.howWillYouUse")}</p>
        </div>

        <div className="space-y-4">
          <button
            onClick={() => handleSelect("customer")}
            disabled={loading}
            className="w-full p-5 bg-white border-2 border-slate-200 rounded-xl hover:border-[#D4AF37] transition-colors text-left flex items-center gap-4 disabled:opacity-50"
            data-testid="role-homeowner"
          >
            <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
              <Home className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="font-bold text-[#0B1F3B]">{t("auth.iAmHomeowner")}</p>
              <p className="text-sm text-slate-500">{t("auth.homeownerDesc")}</p>
            </div>
          </button>

          <button
            onClick={() => handleSelect("business")}
            disabled={loading}
            className="w-full p-5 bg-white border-2 border-slate-200 rounded-xl hover:border-[#D4AF37] transition-colors text-left flex items-center gap-4 disabled:opacity-50"
            data-testid="role-contractor"
          >
            <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
              <Building2 className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <p className="font-bold text-[#0B1F3B]">{t("auth.iAmContractor")}</p>
              <p className="text-sm text-slate-500">{t("auth.contractorDesc")}</p>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}
